import { ref, computed, watch, onMounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import useFilterCounter from "@/components/Filters/useFiltersCount";
import { debounce } from "lodash";
import ability from "@/libs/acl/ability";
import moment from "moment-timezone";

export default function useList(showFilters = true, initialFilters = null) {
  const refListTable = ref(null);

  const toast = useToast();
  const { t } = useI18nUtils();

  const tableColumns = [
    {
      label: "operational_security.operational_processes.folio",
      key: "folio",
      sortable: true,
      thStyle: "min-width: 185px",
    },
    {
      label: "operational_security.operational_processes.sirh",
      key: "unit.sirh",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.unit",
      key: "unit.description",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.rems",
      key: "unit.rems",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.id_seproban",
      key: "unit.id_seproban",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.category",
      key: "unit.category.name",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.state",
      key: "unit.region.name",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.municipality",
      key: "unit.municipality.name",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.sector",
      key: "unit.sector.name",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.activities",
      key: "activities",
      sortable: false,
      thStyle: "min-width: 220px",
    },
    {
      label: "operational_security.operational_processes.system",
      key: "systems",
      sortable: false,
    },
    {
      label: "operational_security.operational_processes.is_test_mode_enabled",
      key: "is_test_mode_enabled_label",
      sortable: true,
      thStyle: "min-width: 120px",
    },
    {
      label: "operational_security.operational_processes.type_of_maintenance",
      key: "type_of_maintenance",
      sortable: true,
      thStyle: "min-width: 200px",
    },
    {
      label: "operational_security.operational_processes.tenant_origin",
      key: "unit.router.tenant.name",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.tenant",
      key: "tenant.name",
      sortable: false,
    },
    {
      label: "operational_security.operational_processes.maintenance_devices",
      key: "device_proxies",
      sortable: false,
      thStyle: "min-width: 240px",
    },
    {
      label: "operational_security.operational_processes.report_number",
      key: "report_number",
      sortable: true,
      thStyle: "min-width: 200px",
    },
    {
      label: "operational_security.operational_processes.company",
      key: "company.name",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.employee",
      key: "employees",
      sortable: false,
    },
    {
      label: "operational_security.operational_processes.status",
      key: "status",
      sortable: true,
    },
    {
      label: "operational_security.operational_processes.user_start",
      key: "user_start.full_name",
      sortable: false,
      thStyle: "min-width: 150px",
    },
    {
      label: "operational_security.operational_processes.check_in_datetime",
      key: "check_in_datetime",
      sortable: true,
      thStyle: "min-width: 200px",
    },
    {
      label: "operational_security.operational_processes.user_finish",
      key: "user_finish.full_name",
      sortable: false,
      thStyle: "min-width: 150px",
    },
    {
      label: "operational_security.operational_processes.check_out_datetime",
      key: "check_out_datetime",
      sortable: true,
      thStyle: "min-width: 200px",
    },
    {
      label: "operational_security.operational_processes.siren_disabled",
      key: "siren_disabled",
      sortable: false,
      thStyle: "min-width: 240px",
    },
    {
      label: "operational_security.operational_processes.siren_enabled",
      key: "siren_enabled",
      sortable: false,
      thStyle: "min-width: 240px",
    },
    {
      label: "operational_security.operational_processes.infrared_height",
      key: "infrared_height",
      sortable: false,
      thStyle: "min-width: 240px",
    },
    {
      label: "operational_security.operational_processes.infrared_validated",
      key: "infrared_validated",
      sortable: false,
      thStyle: "min-width: 200px",
    },
    {
      label: "operational_security.operational_processes.service_time",
      key: "service_time",
      sortable: false,
      thStyle: "min-width: 150px",
    },    
    {
      label: "operational_security.operational_processes.panel_password",
      key: "panel_user_code",
      sortable: false,
    },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
    {
      label: "date.updated_at",
      key: "updated_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
  ];

  if (
    ability.can("update", "ExternalPersonnelAccessControl") ||
    ability.can("delete", "ExternalPersonnelAccessControl") ||
    ability.can("finish_maintenance", "ExternalPersonnelAccessControl")
  ) {
    tableColumns.splice(0, 0, { label: "actions.name", key: "actions" });
  }

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  // Filters
  const folioFilter = ref(initialFilters?.folio ?? null);
  const reportNumberFilter = ref(initialFilters?.report_number ?? null);
  const sirhFilter = ref(initialFilters?.sirh ?? null);
  const remsFilter = ref(initialFilters?.rems ?? null);
  const typeOfMaintenanceFilter = ref(initialFilters?.type_of_maintenance ?? null);
  const isTestModeEnabledFilter = ref(initialFilters?.is_test_mode_enabled ?? null);
  const activitiesFilter = ref(initialFilters?.activities ?? null);
  const unitsFilter = ref(initialFilters?.units ?? null);
  const statusFilter = ref(initialFilters?.status ?? null);
  const createdAtFilter = ref(initialFilters?.created_at ?? null);

  // Items from store (shared across all component instances)
  const localItems = computed(
    () => store.getters["operational_security/getOperationalProcessItems"] || []
  );

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const action = ref("list");
  const disableExport = ref(false);
  const loadingToast = ref(null);

  const trans = {
    "messages.export.download": t("messages.export.download"),
    "messages.export.Generating_file": t("messages.export.Generating_file"),
  };

  // Local fetchList: filter + paginate from localItems
  const fetchList2 = (ctx, callback) => {
    let items = [...localItems.value];

    // Search filter
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase();
      items = items.filter((item) =>
        Object.values(item).some((val) => {
          if (val == null) return false;
          if (Array.isArray(val))
            return val.some((v) => String(v).toLowerCase().includes(q));
          return String(val).toLowerCase().includes(q);
        })
      );
    }

    totalList.value = items.length;

    // Pagination
    const start = (currentPage.value - 1) * perPage.value;
    const end = start + perPage.value;
    const paginatedItems = items.slice(start, end);

    if (typeof callback === "function") callback(paginatedItems);
  };

  const fetchList = (ctx, callback) => {
    store
      .dispatch("external_personnel_access_control/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        action: action.value,
        folio: folioFilter.value,
        report_number: reportNumberFilter.value,
        sirh: sirhFilter.value,
        rems: remsFilter.value,
        type_of_maintenance: typeOfMaintenanceFilter.value,
        is_test_mode_enabled: isTestModeEnabledFilter.value,
        activities: activitiesFilter.value,
        units: unitsFilter.value,
        status: statusFilter.value,
        created_at: createdAtFilter.value,
      })
      .then((response) => {
        if (action.value == "export") {
          action.value = "list";
        } else {
          callback(response.data);
          totalList.value = response.meta.total;
        }
      })
      .catch((error) => {
        console.log({ error });
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const addItem = (formData) => {
    store.commit("operational_security/addOperationalProcessItem", formData);
    refetchData();
  };

  const removeItem = (id) => {
    store.commit("operational_security/removeOperationalProcessItem", id);
    refetchData();
  };

  const refetchData = () => {
    if (refListTable.value) {
      refListTable.value.refresh();
    }
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  // Watch store items — when any instance changes the store, all instances refresh
  watch(
    localItems,
    () => {
      refetchData();
    },
    { deep: true }
  );

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  // ================ filters counter ================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      folio: folioFilter.value,
      report_number: reportNumberFilter.value,
      sirh: sirhFilter.value,
      rems: remsFilter.value,
      type_of_maintenance: typeOfMaintenanceFilter.value,
      is_test_mode_enabled: isTestModeEnabledFilter.value,
      activities: activitiesFilter.value,
      units: unitsFilter.value,
      status: statusFilter.value,
      created_at: createdAtFilter.value,
    };

    refetchData();
  };
  const clearSearchFilters = () => {
    folioFilter.value = null;
    reportNumberFilter.value = null;
    sirhFilter.value = null;
    remsFilter.value = null;
    typeOfMaintenanceFilter.value = null;
    isTestModeEnabledFilter.value = null;
    activitiesFilter.value = null;
    unitsFilter.value = null;
    statusFilter.value = null;
    createdAtFilter.value = null;

    buildFilters();
  };

  onMounted(() => {
    store.dispatch("external_personnel_access_control_activities/getAll");
    store.dispatch("unit/getAll", {
      with: "municipality",
    });
  });

  const activityOptions = computed(() => {
    return store.getters["external_personnel_access_control_activities/getSelectedItems"];
  });

  const unitOptions = computed(() => store.getters["unit/getItems"]);

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  // ==================================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    action,
    disableExport,
    loadingToast,

    // Local data management
    addItem,
    removeItem,
    localItems,

    // filters counter
    filtersCount,
    buildFilters,
    clearSearchFilters,

    //options
    activityOptions,
    unitOptions,

    // Filters
    folioFilter,
    reportNumberFilter,
    sirhFilter,
    remsFilter,
    typeOfMaintenanceFilter,
    isTestModeEnabledFilter,
    activitiesFilter,
    unitsFilter,
    statusFilter,
    createdAtFilter,

    formatDateAssigned,
  };
}

